/**
 * For slugs, where the result is lowercased anyway. ASCII "I" is included
 * deliberately: in Turkish it lowercases to dotless "ı", not "i".
 */
const TR_SLUG_MAP: Record<string, string> = {
  ç: "c",
  Ç: "c",
  ğ: "g",
  Ğ: "g",
  ı: "i",
  I: "i",
  İ: "i",
  i: "i",
  ö: "o",
  Ö: "o",
  ş: "s",
  Ş: "s",
  ü: "u",
  Ü: "u",
  â: "a",
  Â: "a",
  î: "i",
  Î: "i",
  û: "u",
  Û: "u",
};

/**
 * For file names, where case must survive. Only non-ASCII characters are
 * mapped - folding ASCII "I" here would rewrite every DJI_0001.JPG as
 * DJi_0001.JPG and break traceability back to the team's SD card.
 */
const TR_FILE_MAP: Record<string, string> = {
  ç: "c",
  Ç: "C",
  ğ: "g",
  Ğ: "G",
  ı: "i",
  İ: "I",
  ö: "o",
  Ö: "O",
  ş: "s",
  Ş: "S",
  ü: "u",
  Ü: "U",
  â: "a",
  Â: "A",
  î: "i",
  Î: "I",
  û: "u",
  Û: "U",
};

/**
 * ASCII slug for Turkish text.
 *
 * String.toLowerCase() is wrong here: it maps "I" to "i" and leaves "İ" as
 * "i̇" (i + combining dot), so "İzmir" and "Işıklar" both come out broken.
 * Characters are mapped explicitly before any case folding.
 */
export function slugify(input: string): string {
  let out = "";
  for (const ch of input) {
    out += TR_SLUG_MAP[ch] ?? ch;
  }
  return out
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

/**
 * Blob-safe file name. The original name is preserved as far as possible so the
 * delivered data set stays traceable to what the team had on their SD card,
 * but anything outside [A-Za-z0-9._-] is folded away because the server-side
 * path validator rejects it.
 */
export function safeFileName(name: string): string {
  const base = name.split(/[\\/]/).pop() || "file";
  const dot = base.lastIndexOf(".");
  const stem = dot > 0 ? base.slice(0, dot) : base;
  const ext = dot > 0 ? base.slice(dot + 1) : "";

  let cleanStem = "";
  for (const ch of stem) cleanStem += TR_FILE_MAP[ch] ?? ch;
  cleanStem = cleanStem.replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^[._-]+/, "").slice(0, 100);
  if (!cleanStem) cleanStem = "image";

  // Extension case is preserved too - drone SD cards write .JPG, and the
  // delivered set should match what the team sees on disk.
  const cleanExt = ext.replace(/[^A-Za-z0-9]+/g, "").slice(0, 10);
  return cleanExt ? `${cleanStem}.${cleanExt}` : cleanStem;
}
